/**
 * SAP CONFIDENTIAL 
 *
 * This software is confidential proprietary software licensed by SAP SE 
 * and is not to be used, duplicated or disclosed to anyone without the 
 * prior written permission of SAP. 
 *
 * (C) 2020 Copyright SAP SE.
 */
import { Component, NgZone, ViewChild } from '@angular/core';
import { REG_EXP_LIST } from '../../config/constants';
import { CommonService } from '../../shared/services/common.service';
import { LoginService } from '../../components/login/login.service';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { UserProfileService } from './userProfile.component.service';
import { Router } from '@angular/router';
import { NgForm } from '@angular/forms';
import { Subscription } from 'rxjs';


@Component({
    selector: 'user-profile',
    templateUrl: './userProfile.component.html',
    styleUrls: ['./userProfile.component.scss'],
    providers: [UserProfileService]
})

export class UserProfileComponent {
    @ViewChild('accSettingsForm') accSettingsForm!: NgForm;
    public passwordSetting: boolean = false;
    public userProfileDetails: any = {};
    public validPassword = false;
    public validConfirmPassword = false;
    public onloadFlag = true;
    public onloadConfirmFlag = true;
    public languages: any;
    public confirmPassword = false;
    public userName = '';
    public userLogo = '';
    public userGroups: any;
    public toggleAccord: any = { 'account': true, 'security': true };
    public menuObj: any = {};
    public formBaseLogin!: boolean
    public formChangesSubscription: Subscription = new Subscription();
    public formNotChanged = true;

    constructor(
        private translate: TranslateService,
        private commonService: CommonService,
        private toastr: ToastrService,
        private loginService: LoginService,
        private router: Router,
        private zone: NgZone,
        private userProfileService: UserProfileService) {
        this.getUserDetails();
        const menuId = this.commonService.getMenuId('PFL');
        this.commonService.setHelpObj({
            header: this.getTranslationWithKey('PFL') || '',
            menuId: menuId,
            sections: [],
            lastSavedData: []
        });
    }

    ngOnInit() {
        this.getLanaguageDetails();
        this.getUserDetails();
    }

    ngAfterViewInit() {
        //enable the update button when any value in the form changes.
        this.formChangesSubscription = this.accSettingsForm.form.valueChanges.subscribe(() => {
            this.formNotChanged = false;
        })    
    }
    
    ngOnDestroy() {
        //preventing memory leak
        this.formChangesSubscription.unsubscribe();
    }

    public getTranslationWithKey(key: string): string {
        return this.commonService.getTranslatedVal(key);
    }

    private getUserDetails() {
        this.loginService.getUserProfileDetails()
            .then(this.getUserDetailsSH.bind(this), (reason) => this.commonService.commonErrorHandler(reason));
    }

    private getLanaguageDetails() {
        this.userProfileService.getLanaguageDetails()
            .then(this.getLanaguageDetailsSH.bind(this), (reason) => this.commonService.commonErrorHandler(reason));
    }

    private getLanaguageDetailsSH(response: any) {
        this.languages = response;
    }

    private getUserDetailsSH(response: any) {
        const responseMsg = response;
        if (responseMsg) {
            this.loginService.setUser(responseMsg);
            this.userProfileDetails = responseMsg;
            this.userProfileDetails.length = responseMsg.length;
            if (!this.userProfileDetails.language) {
                this.userProfileDetails.language = { id: '' };
            }
            if (!this.userProfileDetails.gender) {
                this.userProfileDetails.gender = 'FEMALE';
            }
            this.userName = this.prepareUserName(this.userProfileDetails);
            this.userGroups = this.prepareUserGroups(this.userProfileDetails);
            this.formBaseLogin = responseMsg.formBaseLogin;
        }
        if(responseMsg.languageId){
            const languageCode = this.getLanguageCode(responseMsg.languageId);
            localStorage.setItem('userLanguage', languageCode);
            this.translate.use(languageCode);
            this.commonService.setSelectedLan(languageCode);
        }
    }

    public updateUserInfo() {
        this.formNotChanged = true; //disable the update button after each submit. 
        this.userProfileService.updateProfileDetails(this.userProfileDetails)
            .then(this.updateUserInfoSH.bind(this), (reason) => this.commonService.commonErrorHandler(reason));
    }

    private updateUserInfoSH(response: any) {
        const responseMsg = response;
        if (responseMsg) {
            if(responseMsg.languageId){
                const languageCode = this.getLanguageCode(responseMsg.languageId);
                const storedLang = localStorage.getItem('userLanguage');
                if (languageCode.toLocaleString() !== storedLang) {
                    this.commonService.getTranslationsOfSelectedLanguage(languageCode).then((res: any) => {                    
                    this.commonService.setTranslatedObj(res);
                    }, (err) => {
                        this.commonService.commonErrorHandler(err);
                        this.commonService.setTranslatedObj([]);                        
                    });
                }
                localStorage.setItem('userLanguage', languageCode);
                this.translate.use(languageCode);                
                this.commonService.setSelectedLan(languageCode);
            }
            this.commonService.setUserDetails(responseMsg);
            this.userName = this.prepareUserName(responseMsg);
            this.toastr.success(this.getTranslationWithKey('user_profile_updated'), this.getTranslationWithKey('success'));
        }
    }

    private getLanguageCode(langId: any){
        const language = this.languages?.find((lang: any) => {return lang.id == langId;});
        return language?.locale;
    }

    public saveNewPassword() {
        const passwordObj: any = {};
        passwordObj.emailId = this.userProfileDetails.emailId;
        passwordObj.currentPwd = btoa(this.userProfileDetails.currentPassword);
        passwordObj.newPwd = btoa(this.userProfileDetails.newPassword);
        this.userProfileService.updatePasswordDetails(passwordObj)
            .then(this.saveNewPasswordSH.bind(this), (reason) => this.commonService.commonErrorHandler(reason));
    }

    private saveNewPasswordSH(response: any) {
        const responseMsg = response;
        if (responseMsg) {
            this.toastr.success(this.getTranslationWithKey('user_password_updated'), this.getTranslationWithKey('success'));
            setTimeout(() => {
                this.zone.run(() => {
                localStorage.clear();
                sessionStorage.clear();
                this.loginService.authenticateLogOutDetails().then((res: any) =>{}, (err)=> {});                
                this.router.navigateByUrl('/login');
            });
            }, 1000);
        }
    }

    public analyzePassword(userProfileDetails: any) {
        if (userProfileDetails.newPassword) {
            const passwordRegex = (userProfileDetails.length && userProfileDetails.length === 16)? new RegExp(REG_EXP_LIST.administratorPassword) : new RegExp(REG_EXP_LIST.defaultPassword);
            this.validPassword = (passwordRegex.test(userProfileDetails.newPassword));
            this.confirmPassword = (userProfileDetails.newPassword === userProfileDetails.currentPassword);
            this.onloadFlag = this.validPassword;
        } else {
	        this.confirmPassword = false
            this.validPassword = true;
            this.onloadFlag = false;
        }
        if (userProfileDetails.confirmNewPassword) {
            this.analyzeConfirmPassword(userProfileDetails);
        }
    }

    public analyzeConfirmPassword(userProfileDetails: any) {
        if (userProfileDetails.confirmNewPassword) {
            this.validConfirmPassword = (userProfileDetails.confirmNewPassword === userProfileDetails.newPassword);
        }
        this.onloadConfirmFlag = false;
    }

    private prepareUserName(user: any) {
        this.userLogo = '';
        const names = [],
            firstName = user ? (user.firstName ? user.firstName : '') : '';
        if (firstName) {
            names.push(firstName);
            this.userLogo = firstName.toString()[0].toUpperCase();
        }
        const lastName = user ? (user.lastName ? user.lastName : '') : '';
        if (lastName) {
            names.push(lastName);
            this.userLogo += lastName.toString()[0].toUpperCase();
        }
        return names.join(' ');
    }

    private prepareUserGroups(user: any) {
        const groups = [];
        if (user.groups && user.groups.length) {
            for (const grp of user.groups) {
                groups.push(grp.name);
            }
        }
        return groups.join(', ');
    }

    public toggleAccordions(panel: any){
        if(panel == 'account'){
            this.toggleAccord.account = !this.toggleAccord.account;
        }else{
            this.toggleAccord.security = !this.toggleAccord.security;
        }
    }

    public updateLangObj(event: any) {
        const langsArr = JSON.parse(JSON.stringify(this.languages))
        const val = event.target.value;
        const valArr = val.split(":");
        const id1 = valArr[1];
        const selectedLang = langsArr.find((lan: any) => {
            return lan.id == id1
        });
        this.userProfileDetails.languageId = id1;
        this.userProfileDetails.language = selectedLang ? selectedLang : this.userProfileDetails.language;
        
    }

    public onPasswordSet() {
        this.passwordSetting = true;
    }
}

